<script>
// Module scope — shared by every instance for the whole visit.
const introState = {seen: false};
</script>

<script setup>
import {useDisplay} from "vuetify";
import {useAssistantPanel} from "~/composables/useAssistantPanel";
import {useFootControls} from "~/composables/useFootControls";
import {isSetupAssistantPath} from "~/utils/setupAssistant";
import {isFlowPageView, nextLandingSkipPath} from "~/utils/clubCountry";
import {mfaHeld} from "~/utils/mfa";
import {tappyWords} from "~/utils/tappy";
import TappyAvatar from "~/components/assistant/TappyAvatar.vue";

// X223 (R92-M1): the layout pauses the nudge while its phone nav drawer is open (it would otherwise sit over the
// drawer's bottom items). Paused hides it without counting as seen; it comes back when the drawer closes.
const props = defineProps({
  paused: {type: Boolean, default: false},
});

/**
 * The Tappy button in the top bar, plus a one-time nudge pointing at it.
 *
 * Both layouts rendered this button identically; it lives here now so the nudge
 * has one home rather than two that can drift.
 *
 * The nudge exists because Tappy is a chat-bubble icon among six others, and
 * nobody goes looking for a feature they don't know shipped. It is shown once and
 * never again — a second showing is nagging, not helping.
 */
const {canUseAssistant, user} = useAuthService();
const {isOpen, toggle} = useAssistantPanel();
const {$locally} = useNuxtApp();

// Grant 2026-11-02: the tooltip in the device's language once the chat has loaded Tappy's greeting
// (useAssistant stores it under this key); English until then. Read only: no request for a tooltip.
const greeting = useState('assistantGreeting', () => null);
const askLabel = computed(() => tappyWords(greeting.value).askLabel);

// Per browser, not per account. A user on a second device seeing it once more is
// a far smaller cost than an endpoint, a column and a migration for a nudge.
const SEEN_KEY = 'assistantIntroSeen';

// introState (module scope, plain <script> above; only ever set on the client) survives the layout
// remounting between admin and public pages even when localStorage is
// unavailable (private mode, blocked site data): the nudge then shows at most
// once per visit instead of on every page.
const hasSeen = () => {
  if (introState.seen) return true;
  try {
    return !!$locally.getItem(SEEN_KEY);
  } catch (e) {
    return false;
  }
};
const markSeen = () => {
  introState.seen = true;
  try {
    $locally.setItem(SEEN_KEY, '1');
  } catch (e) {
    // storage blocked — seenThisVisit still stops it re-appearing this visit
  }
};

const showIntro = ref(false);
let timer = null;
let armed = false;

/**
 * Arm the nudge once the user is known to be eligible.
 *
 * Deliberately not a one-shot check on mount: on a fresh sign-in the layout
 * mounts before the user object lands, so canUseAssistant is still false at that
 * moment and a single check would decide "not eligible" permanently — which is
 * exactly the sign-in case where the nudge matters most.
 */
const arm = () => {
  if (armed || !canUseAssistant.value || hasSeen()) return;
  armed = true;

  // A beat after the user is known: arriving on top of a page still assembling
  // itself reads as an interruption, and gives people something to dismiss
  // before they have looked at what they came for.
  timer = setTimeout(tryShow, 1500);
};

// X221 (R90 P-3): never over a dialog. Vuetify stacks an overlay opened later above every open one, so a coach mark
// that appeared while the country question (or any dialog, sheet or menu) was up drew above it and its scrim. It
// waits until nothing else is open; one opened after it still goes on top, as usual.
const otherOverlayOpen = () => typeof document !== 'undefined' &&
    !!document.querySelector('.v-overlay--active:not(.assistant-intro-menu):not(.v-snackbar)');
const tryShow = () => {
  timer = null;
  if (isOpen.value || onAssistantPage.value || hasSeen()) return;
  if (onFlowPage.value) return; // X225: shown once the visitor leaves the flow page (watch below)
  if (otherOverlayOpen()) { timer = setTimeout(tryShow, 2000); return; }
  showIntro.value = true;
};

// X224 (R93-M1): never on the setup assistant's own page: whoever is there has found the assistant, so arriving counts
// as having met it (seen, for good), and a card already up goes. Client only (markSeen touches storage and the
// module-scope state, which on the server would be shared between requests).
const route = useRoute();
const onAssistantPage = computed(() => isSetupAssistantPath(route.path));
// X225 (R94): never on a sign-in, security or flow page (login, register, /security/* incl. the step-up "Confirm it's
// you", details-required, prompt, invite / join / claim, e-mail and token landings, tv / embed / bare layouts): the
// same list as the country question (utils/clubCountry FLOW_PAGE_PATHS + the X222 sticky landing skip), plus a session
// held by two-step sign-in or account setup. Being there does NOT count as seen: a card already up steps aside
// silently and the nudge comes back (after the usual beat) on the next ordinary page.
const landingSkipPath = ref(nextLandingSkipPath(null, route.path, route.query));
watch(() => route.fullPath, () => { landingSkipPath.value = nextLandingSkipPath(landingSkipPath.value, route.path, route.query); }, {flush: 'sync'});
const onFlowPage = computed(() => isFlowPageView({
  path: route.path,
  query: route.query,
  layout: route.meta?.layout ?? null,
  landingSkipPath: landingSkipPath.value,
}) || mfaHeld(user.value) || !!user.value?.setupRequired);
const showAfterFlowPage = (flow) => {
  if (flow || !armed || showIntro.value || timer || hasSeen()) return;
  timer = setTimeout(tryShow, 1500);
};

const metOnAssistantPage = () => {
  if (!onAssistantPage.value) return;
  if (timer) { clearTimeout(timer); timer = null; }
  showIntro.value = false;
  markSeen();
};

onMounted(() => {
  metOnAssistantPage();
  watch(onAssistantPage, metOnAssistantPage);
  arm();
  watch(onFlowPage, showAfterFlowPage);
  // Client-only, so $locally is real and no timer is ever started during SSR.
  watch(canUseAssistant, arm);
});

onBeforeUnmount(() => {
  if (timer) clearTimeout(timer);
});

const dismiss = () => {
  showIntro.value = false;
  markSeen();
};

// Closed any other way (click outside, Esc) counts as seen too — before, only
// "Got it" saved it, so an outside click brought the nudge back on every page.
// X225: except when it went because the visitor arrived on a flow page (not seen; it comes back after).
watch(showIntro, (open, wasOpen) => {
  if (wasOpen && !open && !onFlowPage.value) markSeen();
});

// Found it on their own — so it has done its job, whoever opened it.
watch(isOpen, (open) => {
  if (open) dismiss();
});

// X221 (R90 P-3): with close-on-back off the card no longer holds a navigation; it goes when the page changes (as any
// other close, it counts as seen).
watch(() => route.path, () => { if (showIntro.value) showIntro.value = false; });

// X222 (R91 O-3): on phones the card anchored under the icon fell over the page's first rows (the Needs you card's
// first row at 390) and took their taps until Got it. Phones get it as a card pinned above the bottom edge instead,
// and the page gains the same room at its foot while it is up, so whatever is under it can still be scrolled clear.
// X223 (R92-M1): it never covers a fixed control either: it stacks above any registered foot control (the club home's
// Setup guide bar) by that control's measured room + a 12px gap, and hides while one of them has a panel open over the
// page or the layout's nav drawer is open (paused). The page already keeps room for its own foot control (.dash--docked),
// so the body only gains the card's own share.
const {xs: phone} = useDisplay();
const {footRoom, footCovered} = useFootControls();
const sheetOpen = computed(() => showIntro.value && phone.value);
const sheetShown = computed(() => sheetOpen.value && !props.paused && !footCovered.value && !onFlowPage.value);
const FOOT_GAP = 12;
const sheetStyle = computed(() => (footRoom.value > 0 ? {bottom: `${footRoom.value + FOOT_GAP}px`} : null));
const SHEET_ROOM = 168;
const sheet = ref(null);
let roomPx = SHEET_ROOM;
const applyRoom = () => {
  if (typeof document === 'undefined') return;
  if (!sheetOpen.value) { document.body.style.paddingBottom = ''; return; }
  const el = sheet.value;
  if (el && sheetShown.value) {
    const rect = el.getBoundingClientRect();
    if (rect.height > 0) roomPx = Math.max(0, Math.ceil(window.innerHeight - rect.top - footRoom.value)) + 8;
  }
  document.body.style.paddingBottom = `${roomPx}px`;
};
watch([sheetOpen, sheetShown, footRoom], () => nextTick(applyRoom));
onBeforeUnmount(() => { if (sheetOpen.value && typeof document !== 'undefined') document.body.style.paddingBottom = ''; });

// The desktop / tablet menu (under the icon) also steps aside while paused, without counting as seen.
const menuOpen = computed({
  get: () => showIntro.value && !props.paused && !onFlowPage.value,
  set: (v) => { if (!v) showIntro.value = false; },
});

const onClick = () => {
  dismiss();
  toggle();
};

// Grant 2026-11-02: the button's Tappy sits still, except for one Hello the moment the one-time nudge appears (the
// card introduces Tappy beside a Tappy saying hello; the button is what it points at).
const buttonAnimation = ref('idle');
watch(showIntro, (open) => {
  if (open) buttonAnimation.value = 'hello';
});
</script>

<template>
  <!-- The v-btn is the component ROOT on purpose: Vue applies the parent's scoped
       style attribute to a child's root element, which is how this keeps the top
       bar's .tl-iconbtn sizing and hairline border. Wrapping it in anything puts
       the button out of that scope and it silently loses its border. -->
  <v-btn
      v-if="canUseAssistant"
      :icon="true"
      class="tl-iconbtn mr-2"
      :aria-label="askLabel"
      @click="onClick"
  >
    <!-- Grant 2026-11-02: Tappy's own face is the icon (no plate: the button is a tile already). -->
    <tappy-avatar :size="28" :plate="false" :blink="false" :animation="buttonAnimation" @settled="buttonAnimation = 'idle'"/>
    <v-tooltip activator="parent" location="bottom">{{ askLabel }}</v-tooltip>

    <!-- X221 (R90 P-3): close-on-back off. On, Vuetify cancels the first Back/Forward while the card is up (to close it)
         and the browser's Forward is lost; the card simply goes with the page instead.
         open-on-click is off: the button toggles the panel, and the activator is
         only here so the card is anchored under the icon it is talking about.
         The menu teleports to the overlay container, so clicking inside it can't
         bubble back into the button. -->
    <v-menu
        v-if="!phone"
        v-model="menuOpen"
        activator="parent"
        :open-on-click="false"
        :close-on-content-click="false"
        location="bottom end"
        offset="8"
        :close-on-back="false"
        content-class="assistant-intro-content"
        class="assistant-intro-menu"
    >
      <div class="assistant-intro pa-4">
        <div class="assistant-intro__head mb-2">
          <tappy-avatar :size="48" animation="hello"/>
          <div class="assistant-intro__title">Meet Tappy</div>
        </div>
        <p class="assistant-intro__body mb-3">
          Ask Tappy how to do anything in Tap Leagues and get a straight answer. It's an AI helper:
          it answers questions and doesn't change your club.
        </p>
        <div class="d-flex justify-end">
          <v-btn size="small" variant="tonal" color="primary" @click="dismiss">Got it</v-btn>
        </div>
      </div>
    </v-menu>

    <!-- X222 (R91 O-3): phones: pinned above the bottom edge, never over the rows under the top bar. -->
    <!-- X223 (R92-M1): above any fixed foot control (style.bottom), hidden while the drawer or an expanded dock is open. -->
    <Teleport v-if="sheetOpen" to="body">
      <div v-show="sheetShown" ref="sheet" class="assistant-intro assistant-intro--sheet pa-4" :style="sheetStyle" role="status" data-testid="assistant-intro-sheet">
        <div class="assistant-intro__head mb-2">
          <tappy-avatar :size="48" animation="hello"/>
          <div class="assistant-intro__title">Meet Tappy</div>
        </div>
        <p class="assistant-intro__body mb-3">
          Ask Tappy how to do anything in Tap Leagues and get a straight answer. It's an AI helper:
          it answers questions and doesn't change your club. Tap the chat bubble at the top to ask.
        </p>
        <div class="d-flex justify-end">
          <v-btn size="small" variant="tonal" color="primary" @click="dismiss">Got it</v-btn>
        </div>
      </div>
    </Teleport>
  </v-btn>
</template>

<style scoped>
.assistant-intro {
  max-width: 280px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
}

/* X222 (R91 O-3): the phone card, above the bottom edge (16px gutters, the home indicator kept clear). Below every
   dialog and sheet (Vuetify overlays start at 2000), above the page and its top bar. */
.assistant-intro--sheet {
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  max-width: none;
  z-index: 1010;
  box-shadow: 0 8px 28px rgba(16, 24, 40, 0.16);
}

/* Grant 2026-11-02: Tappy saying hello beside the card's title (the Intro design). */
.assistant-intro__head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.assistant-intro__title {
  font-weight: 600;
  font-size: 0.95rem;
}

.assistant-intro__body {
  font-size: 0.85rem;
  line-height: 1.4;
  opacity: 0.8;
}
</style>
